import { Button } from 'antd';
import {
  InboxOutlined,
  ExclamationCircleOutlined,
  PlusOutlined,
  ReloadOutlined
} from '@ant-design/icons';

// Three "non-data" presentations the grid can render in place of (or
// inside) the table body: skeleton (loading + empty), empty (no
// loading, no data, no error), and error (any non-null error prop).
//
// Each is intentionally calm — enterprise users see these multiple
// times a day, and a loud empty state quickly becomes noise.

// ── ErpDataGridSkeleton ──
// 8 placeholder rows that mirror the column rhythm. Used when the
// table is loading AND has no data yet (initial load). Once data is
// present, AntD's spinner overlay (still wired in the grid) handles
// the "refreshing existing data" case so the user keeps their place.
export function ErpDataGridSkeleton({ rows = 8, columns = 6 }) {
  const rowKeys = Array.from({ length: rows }, (_, i) => `skel-${i}`);
  const colKeys = Array.from({ length: columns }, (_, i) => `c-${i}`);
  // Vary block widths per column index so the skeleton reads as
  // "column-shaped" not as a flat checkerboard.
  const widths = ['80%', '60%', '70%', '55%', '85%', '40%', '65%', '75%'];
  return (
    <div className="erp-grid__skeleton" aria-busy="true" aria-live="polite">
      {rowKeys.map((rk) => (
        <div key={rk} className="erp-grid__skeleton-row">
          {colKeys.map((ck, idx) => (
            <div key={ck} className="erp-grid__skeleton-cell">
              <span
                className="erp-grid__skeleton-block gi-shimmer"
                style={{ width: widths[idx % widths.length] }}
              />
            </div>
          ))}
        </div>
      ))}
      <span className="sr-only">Loading records…</span>
    </div>
  );
}

// ── ErpDataGridEmpty ──
// Calm empty state. The icon is a slate inbox; CTA is optional and
// only shown if the caller passes one (typically "Add" wired to the
// same handler the toolbar's + Add button uses).
export function ErpDataGridEmpty({
  title = 'No records to display',
  description = 'Nothing matches the current view. Try adjusting filters or add a new record.',
  ctaLabel,
  onCtaClick,
  icon
}) {
  return (
    <div className="erp-grid__state erp-grid__state--empty" role="status">
      <div className="erp-grid__state-icon erp-grid__state-icon--empty">
        {icon || <InboxOutlined />}
      </div>
      <h3 className="erp-grid__state-title">{title}</h3>
      <p className="erp-grid__state-desc">{description}</p>
      {ctaLabel && typeof onCtaClick === 'function' ? (
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={onCtaClick}
          className="erp-grid__state-cta !bg-[#1877F2] hover:!bg-blue-700 !border-0"
        >
          {ctaLabel}
        </Button>
      ) : null}
    </div>
  );
}

// ── ErpDataGridError ──
// Soft-tinted rose card with a retry CTA. Caller controls the message;
// the design assumes a single high-signal error string (we don't dump
// stack traces here — those belong in the dev console / logs).
export function ErpDataGridError({
  title = 'Could not load records',
  message,
  onRetry
}) {
  return (
    <div className="erp-grid__state erp-grid__state--error" role="alert">
      <div className="erp-grid__state-icon erp-grid__state-icon--error">
        <ExclamationCircleOutlined />
      </div>
      <h3 className="erp-grid__state-title">{title}</h3>
      {message ? <p className="erp-grid__state-desc">{message}</p> : null}
      {typeof onRetry === 'function' ? (
        <Button
          icon={<ReloadOutlined />}
          onClick={onRetry}
          className="erp-grid__state-cta"
        >
          Try again
        </Button>
      ) : null}
    </div>
  );
}
